import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import styled from 'styled-components';
import { COLORS } from '../Styles/Colors';
import { IoIosPin } from 'react-icons/io';
import { Company, CompanyFormInput } from '../Types';
import CompanyForm from './Company/CompanyForm';
import BulkImportCompany from './Company/BulkImportCompany';
import useGetData from '../Hooks/useGetData';
import { AddButton, CancelButton, CloseButton, DeleteButton, DeleteConfirmButton, EditButton, ErrorMessage, GridContainer, LoadingMessage, Message, ModalContent, ModalHeader, ModalOverlay, PageContainer, RecordCount, TealSwitch, Title } from '../Styles/AppStyles';
import apiClient from '../Services/ApiClient';
import { PencilIcon } from 'lucide-react';
import { decode } from '../Services/DecodeToken';
import { useSnackbar } from '../Context/SnackbarProvider';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { Header } from '../Components/Header/Header';

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;



const CompanyPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [companyToEdit, setCompanyToEdit] = useState<Company | null>(null);

  const [companyData, loading, errorFetchCompany, refetch] = useGetData<Company>(`/api/company/user`);

  const handleCloseModal = () => {
    setShowAddModal(false);
    setCompanyToEdit(null);
  };

  const handleImportComplete = (importedCompany: Company[]) => {
    setCompanies([...companies, ...importedCompany]);
    showSnackbar(`Successfully imported ${importedCompany.length} assets`, 'success', 'Import Complete');
    setShowImportModal(false);
  };

  const handleImportError = (errorMsg: string) => {
    showSnackbar(errorMsg, 'error', 'Import Failed');
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const handleToggleActive = async (company: Company) => {
    try {
      const response = await apiClient(`/api/company/${company.CID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...company, IsActive: !company.IsActive })
      });

      if (!response.ok) {
        const errorData = await response.json();

        showSnackbar(errorData.error || 'Failed to update company', "error", "Something went wrong!")
      }

      showSnackbar(`Successfully updated ${company.name}`, 'success', 'Updated');
      refetch();
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Something went wrong!');
    }
  };

  const handleConfirmDelete = async () => {
    if (!companyToDelete) return;

    try {
      const response = await apiClient(`/api/company/${companyToDelete.CID}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to update company', "error", "Something went wrong!")
      }

      showSnackbar(`Successfully deleted ${companyToDelete.name}`, 'success', 'Deleted');
      refetch();
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Delete Failed');
    } finally {
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    }
  };

  const handleSubmit = async (company: CompanyFormInput) => {
    try {
      const url = companyToEdit ? `/api/company/${companyToEdit.CID}` : '/api/company';
      const method = companyToEdit ? 'PUT' : 'POST';

      const response = await apiClient(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(company),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to update company', "error", "Something went wrong!")
      }

      showSnackbar(
        companyToEdit ? 'User updated successfully!' : 'User added successfully!',
        'success',
        companyToEdit ? 'Updated' : 'Added'
      );

      refetch();
      setShowAddModal(false);
      setCompanyToEdit(null);
    } catch (err: any) {
      showSnackbar(err.message, 'error', 'Submission Failed');
    }
  };

   const columnsNew: ColumnDef<any>[] = [
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: "center", marginTop: 12 }}>
          <EditButton onClick={() => {
            setCompanyToEdit(params.row);
            setShowAddModal(true);
          }}>
            <PencilIcon />
          </EditButton>
        </div>
      )
    },
    { field: 'name', headerName: 'Company Name', width: 200, searchable: true },
    { field: 'P21CustomerID', headerName: 'P21 Customer ID', width: 150, searchable: true },
    { field: 'OrganizationID', headerName: 'Organization ID', width: 120, searchable: true },
    { field: 'IsP21Company', headerName: 'P21 Company', width: 100, type: 'boolean' },
    { field: 'IsConsolidatedInvoice', headerName: 'Consolidated Invoice', width: 150, type: 'boolean' },
    { field: 'IsAutoInvoice', headerName: 'Auto Invoice', width: 100, type: 'boolean' },
    { field: 'IsTrustedCustomer', headerName: 'Trusted', width: 120, type: 'boolean' },
    { field: 'PriceVisibleFlag', headerName: 'Price Visible', width: 150, type: 'boolean' },
    { field: 'BillingDay', headerName: 'Billing Day', width: 150 },
    { field: 'BillingTime', headerName: 'Billing Time', width: 150 },
    { field: 'SpendingLimitEnableFlag', headerName: 'Spending Limit Enable', width: 180, type: 'boolean' },
    { field: 'CreatedDate', headerName: 'Created', width: 120 },
    { field: 'ModifiedDate', headerName: 'Modified', width: 120 },
    { field: 'ModifiedBy', headerName: 'Modified By', width: 120 },
    {
      field: 'IsActive',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <TealSwitch
          checked={params.row.IsActive}
          onChange={() => handleToggleActive(params.row)}
          color="primary"
        />
      )
    }
  ];

  const columns: GridColDef<Company>[] = [
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: "center", marginTop: 12 }}>
          <EditButton onClick={() => {
            setCompanyToEdit(params.row);
            setShowAddModal(true);
          }}>
            <PencilIcon />
          </EditButton>
        </div>
      )
    },
    { field: 'name', headerName: 'Company Name', width: 200 },
    { field: 'P21CustomerID', headerName: 'P21 Customer ID', width: 150 },
    { field: 'OrganizationID', headerName: 'Organization ID', width: 120 },
    { field: 'IsP21Company', headerName: 'P21 Company', width: 100, type: 'boolean' },
    { field: 'IsConsolidatedInvoice', headerName: 'Consolidated Invoice', width: 150, type: 'boolean' },
    { field: 'IsAutoInvoice', headerName: 'Auto Invoice', width: 100, type: 'boolean' },
    { field: 'IsTrustedCustomer', headerName: 'Trusted', width: 120, type: 'boolean' },
    { field: 'PriceVisibleFlag', headerName: 'Price Visible', width: 150, type: 'boolean' },
    { field: 'BillingDay', headerName: 'Billing Day', width: 150 },
    { field: 'BillingTime', headerName: 'Billing Time', width: 150 },
    { field: 'SpendingLimitEnableFlag', headerName: 'Spending Limit Enable', width: 180, type: 'boolean' },
    { field: 'CreatedDate', headerName: 'Created', width: 120 },
    { field: 'ModifiedDate', headerName: 'Modified', width: 120 },
    { field: 'ModifiedBy', headerName: 'Modified By', width: 120 },
    {
      field: 'IsActive',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <TealSwitch
          checked={params.row.IsActive}
          onChange={() => handleToggleActive(params.row)}
          color="primary"
        />
      )
    }
  ];

  return (
    <PageContainer>


      <Header title="Company Management" data={companyData}>
      <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>

        </Header>

  
     


    {showAddModal && (
        <ModalOverlay>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            <ModalHeader>{companyToEdit ? "Edit" : "Add"} Company</ModalHeader>
            <CompanyForm onSubmit={handleSubmit} defaultValues={companyToEdit ?? undefined} onCancel={handleCloseModal} />
          </ModalContent>
        </ModalOverlay>
      )}


  {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <BulkImportCompany onImportComplete={handleImportComplete} onError={handleImportError}  />
          </ModalContent>
        </ModalOverlay>
    )}

  {showDeleteModal && companyToDelete && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setShowDeleteModal(false)}>×</CloseButton>
            <ModalHeader>Confirm Deletion</ModalHeader>
            <p>Are you sure you want to delete the company: <strong>{companyToDelete.name}</strong>?</p>
            
            <div style={{display: "flex", justifyContent: "center", gap: 5, alignItems: "center"}}>
              <CancelButton onClick={() => setShowDeleteModal(false)}>Cancel</CancelButton>
              <DeleteConfirmButton onClick={handleConfirmDelete}>Delete</DeleteConfirmButton>
            </div>
          </ModalContent>
        </ModalOverlay>
    )}


    <DataTable
      data={companyData}
      columns={columnsNew}
      pageKey="company"
      defaultSort={[{ field: 'name', sort: 'asc' }]}
      title="Companies"
      subtitle="Manage your companies here"
      loading={loading}
      onRowClick={(row) => {
        setCompanyToEdit(row);
      }}
      initialPageSize={10}
      pageSizeOptions={[5, 10, 20]}
      idField="CID"
    />

  
   
    </PageContainer>
  );
};

export default CompanyPage;